import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Whitelist } from "../Interfaces/interfaces";
import WhitelistTable from "../Components/Tables/whitelistTable";
import WhitelistFilters from "../Components/FilterBars/whitelistFilters";
import { useYouTubeScorecardData } from "../../../hooks/useYouTubeScorecardData";
import LoadingState from "../Components/LoadingState";
import ErrorState from "../Components/ErrorState";
import {
  buildQueryString,
  parseStringArrayParam,
  parseStringParam,
} from "../../../utils/urlFilters";

const WhitelistTab: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    data: rows,
    loading,
    error,
    refreshData,
  } = useYouTubeScorecardData("whitelist");

  const [filters, setFilters] = useState<any>({});
  const [filteredRows, setFilteredRows] = useState<Whitelist[]>(rows as Whitelist[]);

  const applyFilters = useCallback(
    (filters: any) => {
      const filtered = (rows as Whitelist[]).filter((row) => {
        let includeRow = true;

        // Property Filter
        if (filters.selectedProperty?.length) {
          includeRow = filters.selectedProperty.includes(row.property);
        }

        // Condition Filter
        if (filters.selectedCondition) {
          includeRow = includeRow && filters.selectedCondition.includes(row.condition);
        }

        if (filters.selectedValue) {
          includeRow = includeRow && filters.selectedValue.includes(row.value);
        }

        // Fields Filter
        if (filters.selectedFields?.length) {
          includeRow =
            includeRow &&
            filters.selectedFields.every((field: string) =>
              row.fields.includes(field)
            );
        }

        return includeRow;
      });

      setFilteredRows(filtered);
    },
    [rows]
  );

  useEffect(() => {
    applyFilters(filters);
  }, [rows, filters, applyFilters]);

  const handleFilterChange = useCallback((newFilters: any) => {
    setFilters(newFilters);
  }, []);

  const handleSearch = useCallback(
    (nextFilters: any) => {
      setFilters(nextFilters);
      const query = buildQueryString({
        property: nextFilters.selectedProperty,
        condition: nextFilters.selectedCondition,
        value: nextFilters.inputedValue,
        audit: nextFilters.selectedAudit,
        fields: nextFilters.selectedFields,
      });
      navigate({ search: query }, { replace: true });
    },
    [navigate]
  );

  const handleReset = useCallback(
    (nextFilters: any) => {
      setFilters(nextFilters);
      navigate({ search: "" }, { replace: true });
    },
    [navigate]
  );

  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      property: parseStringArrayParam(params, "property"),
      condition: parseStringParam(params, "condition"),
      value: parseStringParam(params, "value"),
      audit: parseStringParam(params, "audit"),
      fields: parseStringArrayParam(params, "fields"),
    };
  }, [location.search]);

  if (loading) {
    return <LoadingState message="Loading whitelist data..." />;
  }

  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={refreshData}
        title="Error Loading Whitelist Data"
      />
    );
  }

  return (
    <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
      <div>
        <Card
          className="flex-column md:flex-col gap-3"
          style={{ marginTop: "0.3rem", marginBottom: "-10px" }}
        >
          <WhitelistFilters
            rows={rows as Whitelist[]}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onReset={handleReset}
            initialProperty={initialFilters.property}
            initialCondition={initialFilters.condition}
            initialValue={initialFilters.value}
            initialAudit={initialFilters.audit}
            initialFields={initialFilters.fields}
          />
        </Card>
        <WhitelistTable rows={filteredRows} loading={loading} />
      </div>
    </div>
  );
};

export default WhitelistTab;
